import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Grid, Card, CardContent, CircularProgress, Pagination, Chip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { fetchProductsByCategory, fetchCategories } from "../api/service";
import ProductModal from "../components/product/ProductModal";
import { HOST_URL } from "../config/Url";
import SafeImage from '../components/SafeImage';

const CategoryPage = () => {
  const { categoryId } = useParams(); // This is actually the category slug from the URL
  const navigate = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [currentCategoryName, setCurrentCategoryName] = useState("");
  const [actualCategoryId, setActualCategoryId] = useState(null);

  // Fetch all categories to find the proper category ID and name
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories
  });

  // Find and set current category ID and name from slug
  useEffect(() => {
    if (categories && categoryId) {
      const category = categories.find(
        (cat) =>
          cat.slug === categoryId ||
          String(cat.id) === String(categoryId)
      );
      if (category) {
        setCurrentCategoryName(category.name);
        setActualCategoryId(category.id);
        // console.log(`Found category: ${category.name}, ID: ${category.id} for slug: ${categoryId}`);
      } else {
        // Fallback to formatted slug if category not found
        setCurrentCategoryName(
          categoryId.split("-").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
        );
        setActualCategoryId(null);
        // console.log(`Category not found for slug: ${categoryId}, using formatted slug`);
      }
    }
  }, [categories, categoryId]);

  // Reset page to 1 when category changes
  useEffect(() => {
    setPage(1);
  }, [categoryId]);

  // Properly handle the category ID with additional safeguards
  const { 
    data, 
    isLoading: productsLoading, 
    isError
  } = useQuery({
    queryKey: ["categoryProducts", actualCategoryId, page],
    queryFn: () => {
      if (!actualCategoryId) {
        throw new Error("Category ID is undefined");
      }
      console.log(`Fetching products for category ID: ${actualCategoryId}`);
      return fetchProductsByCategory({ 
        category: actualCategoryId, // Using the actual category ID here
        page, 
        pageSize 
      }).then(result => {
        // Call debug function with the results
        if (result?.products) {
          debugProductData(result.products);
        }
        return result;
      });
    },
    enabled: !!actualCategoryId, // Only run query when we have the actual category ID
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    onError: (error) => {
      console.error(`Error fetching products for category ${actualCategoryId}:`, error);
    }
  });

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleOpenModal = (product) => {
    setSelectedProduct(product);
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
  };

  // Debug function to log product data and image structure
  const debugProductData = (products) => {
    if (products && products.length > 0) {
      console.log('Sample product data:', products[0]);
      
      if (products[0].fileResources && products[0].fileResources.length > 0) {
        console.log('Sample file resource:', products[0].fileResources[0]);
        console.log('Image URL constructed:', `${HOST_URL}/${products[0].fileResources[0].filePath}`);
      } else {
        console.log('No file resources found for sample product');
      }
    }
  };

  // Create a helper function to get the correct image URL, using a filter approach like ProductModal
  const getProductImageUrl = (product) => {
    if (!product.fileResources || product.fileResources.length === 0) {
      return null;
    }
    
    // Filter for image files based on fileType, similar to ProductModal component
    const imageFiles = product.fileResources.filter((file) =>
      ["png", "jpg", "jpeg"].includes(file.fileType?.toLowerCase())
    );
    
    // Return URL if valid image file exists, otherwise null
    if (imageFiles.length > 0) {
      const imageUrl = `${HOST_URL}/${imageFiles[0].filePath}`;
      return imageUrl;
    }
    
    return null;
  };

  // Display loading state
  const isLoading = categoriesLoading || productsLoading;
  if (isLoading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        flexDirection="column"
        height="400px"
      >
        <CircularProgress size={60} thickness={4} sx={{ color: "#11A193", mb: 3 }} />
        <Typography variant="body1" color="textSecondary">
          {categoriesLoading ? "Loading category information..." : 
            `Loading products for ${currentCategoryName || categoryId}...`}
        </Typography>
      </Box>
    );
  }

  // Error handling for missing category or API errors
  if ((isError && actualCategoryId) || (!actualCategoryId && !categoriesLoading)) {
    return (
      <Box 
        textAlign="center" 
        color="error.main" 
        py={6} 
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Typography variant="h5" gutterBottom>
          Error loading products
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
          {!actualCategoryId ? "Category not found" : `Failed to load products for category: ${currentCategoryName}`}
        </Typography>
        <Chip 
          label="Go back to home" 
          onClick={() => navigate("/")} 
          sx={{ 
            bgcolor: '#014b3b', 
            color: 'white',
            '&:hover': {
              bgcolor: '#11A193',
            }
          }} 
        />
      </Box>
    );
  }

  // Check if products exist
  if (!data?.products || data.products.length === 0) {
    return (
      <Box textAlign="center" my={4}>
        <Typography variant="h5" gutterBottom>
          {currentCategoryName || (categoryId ? categoryId.split("-").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ") : "")}
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
          No products found in this category.
        </Typography>
        <Chip 
          label="Go back to home" 
          onClick={() => navigate("/")} 
          sx={{ 
            bgcolor: '#014b3b', 
            color: 'white',
            '&:hover': {
              bgcolor: '#11A193',
            }
          }} 
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3,
        pb: 1,
        borderBottom: '1px solid rgba(0,0,0,0.1)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* <Chip
            label={`${data.totalResults} Products`}
            sx={{
              bgcolor: 'rgba(17, 161, 147, 0.1)',
              color: '#11A193',
              fontWeight: 'bold'
            }}
          /> */}
          <Typography variant="h4" component="h1">
            {currentCategoryName || (
              categoryId ? categoryId.split("-").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ") : ""
            )}
          </Typography>
        </Box>
        <Box></Box>
      </Box>

      {/* Products Grid */}
      <Grid container spacing={3}>
        {data.products.map((product) => (
          <Grid item xs={12} sm={6} md={4} key={product.id}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                borderRadius: '12px',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                }
              }}
              onClick={() => handleOpenModal(product)}
            >
              <Box
                sx={{
                  height: 200,
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                <SafeImage 
                  src={getProductImageUrl(product)}
                  alt={product.brandName || "Product"}
                  style={{width: '100%', height: '100%', objectFit: 'cover'}}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(0deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 50%)',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    '&:hover': {
                      opacity: 1
                    }
                  }}
                />
              </Box>
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                <Typography 
                  gutterBottom 
                  variant="h6" 
                  component="h2"
                  sx={{ 
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    color: '#014b3b',
                    mb: 0.5
                  }}
                >
                  {product.brandName}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{
                    fontSize: '0.875rem',
                    mb: 1
                  }}
                >
                  {product.genericName}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 'auto',
                    pt: 1,
                    borderTop: '1px dashed rgba(0,0,0,0.1)'
                  }}
                >
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#11A193',
                      fontWeight: 500
                    }}
                  >
                    View Details
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Pagination */}
      {data.totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 4 }}>
          <Pagination
            count={data.totalPages || 1}
            page={page}
            onChange={handlePageChange}
            size="large"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                color: '#014b3b',
              },
              '& .Mui-selected': {
                backgroundColor: '#11A193 !important',
                color: 'white !important',
                fontWeight: 'bold',
              },
              '& .MuiPaginationItem-page:hover': {
                backgroundColor: 'rgba(17, 161, 147, 0.2)',
              },
              '& .MuiPaginationItem-firstLast, & .MuiPaginationItem-previousNext': {
                color: '#014b3b',
              }
            }}
          />
        </Box>
      )}

      {/* Product Modal */}
      <ProductModal product={selectedProduct} handleClose={handleCloseModal} />
    </Box>
  );
};

export default CategoryPage;
